export default class ClusterInspector {
  init(root) {
    this.element = document.createElement('aside')
    this.element.className = 'inspector-panel'
    root.appendChild(this.element)
    this.update({ selectedVibe: null, selectedTerritory: null, vibeTerritory: null, nodeInterpretation: null })
  }

  update(context = {}) {
    if (!this.element) {
      return
    }

    const {
      selectedVibe = null,
      selectedTerritory = null,
      vibeTerritory = null,
      nodeInterpretation = null,
      evolutionLabState = null,
      selectedTerritoriesForHybridization = []
    } = context

    if (selectedVibe) {
      this.renderNodeInspector(selectedVibe, vibeTerritory, nodeInterpretation, evolutionLabState)
      return
    }

    if (selectedTerritory) {
      this.renderTerritoryInspector(selectedTerritory, evolutionLabState)
      return
    }

    this.element.innerHTML = `
      <div class="inspector-panel__title">SYMBOLIC SCANNER</div>
      <p class="inspector-panel__empty">Click a node to inspect its emotional function, or click a territory label to scan the wider symbolic climate.</p>
    `
  }

  renderNodeInspector(selectedVibe, territory, interpretation, evolutionLabState) {
    const axisList = Object.entries(selectedVibe.axes ?? {})
      .sort((left, right) => right[1] - left[1])
      .slice(0, 4)
      .map(([axis, value]) => `<li>${this.formatAxis(axis)} <strong>${Number(value).toFixed(0)}</strong></li>`)
      .join('')

    const nearestNeighbors = (interpretation?.nearestEmotionalNeighbors ?? [])
      .map(entry => `<li>${entry.name} <strong>${Math.round(entry.score * 100)}%</strong></li>`)
      .join('')

    const contributionAxes = (interpretation?.dominantContributionAxes ?? [])
      .map(axis => `<li>${axis}</li>`)
      .join('')

    const territoryContext = territory
      ? `
        <div class="inspector-panel__section">
          <span>Territory</span>
          <strong>${territory.title}</strong>
        </div>
        <div class="inspector-panel__section">
          <span>Regional climate</span>
          <strong>${territory.symbolicClimate}</strong>
        </div>
      `
      : ''

    // Evolution lab info for nodes
    const evolutionInfo = evolutionLabState?.isExperimentalMode 
      ? `
        <div class="inspector-panel__section inspector-panel__section--stacked">
          <span>Evolution Mode</span>
          <p>Node behavior may shift under active emotional modifiers</p>
        </div>
      ` : ''

    this.element.innerHTML = `
      <div class="inspector-panel__title">EMOTIONAL CARTOGRAPHY CONSOLE</div>
      <div class="inspector-panel__name">${selectedVibe.name}</div>
      ${territoryContext}
      <div class="inspector-panel__section">
        <span>Coordinates</span>
        <strong>${Number(selectedVibe.x).toFixed(1)}, ${Number(selectedVibe.y).toFixed(1)}</strong>
      </div>
      ${evolutionInfo}
      <div class="inspector-panel__section inspector-panel__section--stacked">
        <span>Why it belongs</span>
        <p>${interpretation?.whyNodeBelongs || `${selectedVibe.name} is still being read against the wider field.`}</p>
      </div>
      <div class="inspector-panel__section">
        <span>Symbolic role</span>
        <strong>${interpretation?.symbolicRoleInsideTerritory || 'Unresolved carrier'}</strong>
      </div>
      <div class="inspector-panel__section">
        <span>Contribution score</span>
        <strong>${this.formatPercent(interpretation?.emotionalContributionScore)}</strong>
      </div>
      <div class="inspector-panel__section">
        <span>Territory influence</span>
        <strong>${this.formatPercent(interpretation?.territoryInfluenceStrength)}</strong>
      </div>
      <div class="inspector-panel__grid">
        <div>
          <div class="inspector-panel__label">Emotional axes</div>
          <ul>${axisList}</ul>
        </div>
        <div>
          <div class="inspector-panel__label">Nearest neighbors</div>
          <ul>${nearestNeighbors || '<li>no nearby emotional matches</li>'}</ul>
        </div>
      </div>
      <div class="inspector-panel__grid">
        <div>
          <div class="inspector-panel__label">Contribution family</div>
          <ul>${contributionAxes || '<li>subtle peripheral influence</li>'}</ul>
        </div>
        <div>
          <div class="inspector-panel__label">Regional motifs</div>
          <ul>${(territory?.dominantSymbolicMotifs ?? []).map(motif => `<li>${motif}</li>`).join('') || '<li>motifs still condensing</li>'}</ul>
        </div>
      </div>
    `
  }

  renderTerritoryInspector(territory, evolutionLabState) {
    const dominantAxes = (territory.dominantAxes ?? [])
      .map(entry => `<li>${entry.label} <strong>${Number(entry.value).toFixed(0)}</strong></li>`)
      .join('')

    const vibeFamilies = (territory.dominantVibeFamilies ?? [])
      .map(name => `<li>${name}</li>`)
      .join('')

    const motifs = (territory.dominantSymbolicMotifs ?? [])
      .map(item => `<li>${item}</li>`)
      .join('')

    const neighbors = (territory.strongestNeighborTerritories ?? [])
      .map(neighbor => `<li>${neighbor.title} — ${neighbor.relation}</li>`)
      .join('')

    const conflicts = (territory.internalSymbolicConflicts ?? [])
      .map(item => `<li>${item}</li>`)
      .join('')

    // Evolution lab info for territories
    const evolutionInfo = evolutionLabState?.isExperimentalMode 
      ? `
        <div class="inspector-panel__section inspector-panel__section--stacked">
          <span>Experimental Mode Active</span>
          <p>This territory is being analyzed for potential evolution pathways</p>
        </div>
      ` : ''

    // Hybridization info
    const hybridizationInfo = evolutionLabState?.selectedTerritories?.has(territory.id)
      ? `
        <div class="inspector-panel__section inspector-panel__section--stacked">
          <span>Selected for Hybridization</span>
          <p>This territory is part of an active hybridization experiment</p>
        </div>
      ` : ''

    this.element.innerHTML = `
      <div class="inspector-panel__title">SYMBOLIC SCANNER</div>
      <div class="inspector-panel__name">${territory.title}</div>
      <div class="inspector-panel__subtitle">${territory.symbolicArchetype}</div>
      ${hybridizationInfo}
      ${evolutionInfo}
      <div class="inspector-panel__section">
        <span>Dominant profile</span>
        <strong>${territory.dominantEmotionalProfile}</strong>
      </div>
      <div class="inspector-panel__section">
        <span>Cultural role</span>
        <strong>${territory.culturalRole}</strong>
      </div>
      <div class="inspector-panel__section">
        <span>Symbolic climate</span>
        <strong>${territory.symbolicClimate}</strong>
      </div>
      <div class="inspector-panel__section">
        <span>Social cohesion</span>
        <strong>${territory.socialCohesion.label} · ${this.formatPercent(territory.socialCohesion.score)}</strong>
      </div>
      <div class="inspector-panel__section">
        <span>Memetic stability</span>
        <strong>${territory.memeticStability.label} · ${this.formatPercent(territory.memeticStability.score)}</strong>
      </div>
      <div class="inspector-panel__section inspector-panel__section--stacked">
        <span>Regional tension</span>
        <p>${territory.regionalTension}</p>
      </div>
      <div class="inspector-panel__section inspector-panel__section--stacked">
        <span>Formation reading</span>
        <p>${territory.formationReason}</p>
      </div>
      <div class="inspector-panel__section inspector-panel__section--stacked">
        <span>Narrative description</span>
        <p>${territory.narrativeDescription}</p>
      </div>
      <div class="inspector-panel__grid">
        <div>
          <div class="inspector-panel__label">Dominant axes</div>
          <ul>${dominantAxes}</ul>
        </div>
        <div>
          <div class="inspector-panel__label">Vibe families</div>
          <ul>${vibeFamilies}</ul>
        </div>
      </div>
      <div class="inspector-panel__grid">
        <div>
          <div class="inspector-panel__label">Strongest neighbors</div>
          <ul>${neighbors || '<li>no significant neighboring climates</li>'}</ul>
        </div>
        <div>
          <div class="inspector-panel__label">Internal conflicts</div>
          <ul>${conflicts || '<li>no major internal conflict detected</li>'}</ul>
        </div>
      </div>
      <div class="inspector-panel__grid inspector-panel__grid--single">
        <div>
          <div class="inspector-panel__label">Dominant symbolic motifs</div>
          <ul>${motifs}</ul>
        </div>
      </div>
    `
  }

  formatAxis(axis) {
    return axis
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, char => char.toUpperCase())
  }

  formatPercent(value) {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return '—'
    }

    return `${Math.round(value * 100)}%`
  }
}